import { useState } from 'react';
import { pathToUrl } from 'shared/lib/router';
import { api } from 'shared/lib/api';
import { showDefaultErrorToast } from 'shared/lib/notifications';

interface DownloadFileParams {
  url: string;
  urlFilters?: string;
  type?: 'zip' | 'text' | 'json' | 'pdf';
}
export const useDownloadFile = ({
  url,
  urlFilters,
  type = 'text',
}: DownloadFileParams): {
  isLoading: boolean;
  download: (paramsForUrl?: Record<string, string>) => Promise<void>;
  openInNewWindow: (paramsForUrl?: Record<string, string>) => Promise<void>;
} => {
  const [isLoading, setIsLoading] = useState(false);

  const isText = type === 'text';
  const isJson = type === 'json';
  const isZip = type === 'zip';
  const isPdf = type === 'pdf';

  const requestData = (paramsForUrl?: Record<string, string>) => {
    setIsLoading(true);

    const params = { responseType: isZip || isPdf ? 'blob' : 'text' };
    const currentUrl = paramsForUrl ? pathToUrl(url, paramsForUrl) : url;
    const composedUrl = `${currentUrl}${urlFilters ?? ''}`;

    return api.get(composedUrl, params).then((res) => {
      return res;
    });
  };

  const download = (paramsForUrl?: Record<string, string>) => {
    return requestData(paramsForUrl)
      .then((res) => {
        const fileName = res.headers['content-disposition']?.split('filename=')[1] ?? 'filename';
        const element = document.createElement('a');

        isText && (element.href = `data:text/plain;charset=utf-8,${encodeURIComponent(res.data as string)}`);
        isJson && (element.href = `data:text/plain;charset=utf-8,${encodeURIComponent(JSON.stringify(res.data))}`);
        (isZip || isPdf) && (element.href = URL.createObjectURL(res.data as Blob));

        element.download = fileName;
        element.style.display = 'none';
        element.click();
        element.remove();
      })
      .catch((err) => {
        showDefaultErrorToast();

        throw new Error(`Can't download file`, err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const openInNewWindow = (paramsForUrl?: Record<string, string>) => {
    return requestData(paramsForUrl)
      .then((res) => {
        let file = '';

        isText && (file = `data:text/plain;charset=utf-8,${encodeURIComponent(res.data as string)}`);
        isJson && (file = `data:text/plain;charset=utf-8,${encodeURIComponent(JSON.stringify(res.data))}`);
        (isZip || isPdf) && (file = URL.createObjectURL(res.data as Blob));

        window.open(file, '_blank');
      })
      .catch((err) => {
        showDefaultErrorToast();

        throw new Error(`Can't open file`, err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return { isLoading, download, openInNewWindow };
};
